import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { AlertTriangle, Archive, MessageCircle } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Card } from '../ui/card';
import cn from 'classnames';

interface Author {
  student_name: string;
  avatar_url: string | null;
}

interface Comment {
  id: string;
  content: string;
  created_at: string;
  user_id: string;
  author: Author;
}

interface ParentCommunityPostProps {
  id: string;
  content: string;
  created_at: string;
  author: Author;
  archived?: boolean;
  archived_at?: string;
  archived_reason?: string;
  comments?: Comment[];
}

export const ParentCommunityPost = ({
  content,
  created_at,
  author,
  archived,
  archived_at,
  archived_reason,
  comments = []
}: ParentCommunityPostProps) => {
  return (
    <Card className={cn(
      "p-4 relative",
      archived ? "border-l-4 border-l-yellow-400 bg-gray-50" : ""
    )}>
      {/* Badge de Archivado */}
      {archived && (
        <div className="absolute top-2 right-2 flex items-center gap-2 text-sm text-gray-500 bg-yellow-50 px-3 py-1 rounded-full">
          <Archive className="w-4 h-4" />
          <span>Archivado</span>
        </div>
      )}

      {/* Información de Archivado */}
      {archived && archived_at && (
        <div className="mb-4 p-3 bg-yellow-50 rounded-md text-sm">
          <div className="flex items-start gap-2 text-yellow-700">
            <AlertTriangle className="w-5 h-5 mt-0.5" />
            <div>
              <p className="font-medium">Esta publicación ha sido archivada por un moderador</p>
              <p className="text-yellow-600 mt-1">
                Fecha: {format(new Date(archived_at), "d 'de' MMMM 'de' yyyy 'a las' HH:mm", { locale: es })}
              </p>
              {archived_reason && (
                <p className="mt-1">
                  <span className="font-medium">Motivo:</span> {archived_reason}
                </p>
              )}
              <p className="mt-2 text-yellow-600 text-xs">
                Las publicaciones archivadas solo son visibles en el panel de control parental.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Contenido Principal */}
      <div className="flex gap-3">
        <Avatar className="w-10 h-10">
          <AvatarImage src={author.avatar_url || undefined} />
          <AvatarFallback>{author.student_name[0]}</AvatarFallback>
        </Avatar>

        <div className="flex-1">
          <div className="flex justify-between items-start mb-1">
            <h3 className="font-medium text-sm">{author.student_name}</h3>
            <span className="text-xs text-gray-500">
              {format(new Date(created_at), "d 'de' MMMM 'de' yyyy", { locale: es })}
            </span>
          </div>

          <p className="text-gray-800 text-sm whitespace-pre-wrap">{content}</p>

          {/* Contador de Comentarios */}
          <div className="mt-3 flex items-center text-gray-500 text-sm">
            <MessageCircle className="w-4 h-4 mr-1" />
            <span>{comments.length} comentarios</span>
          </div>
        </div>
      </div>

      {/* Lista de Comentarios */}
      {comments.length > 0 && (
        <div className="mt-4 pl-13 space-y-3">
          {comments.map((comment) => (
            <div
              key={comment.id}
              className={cn(
                "p-3 rounded-md text-sm",
                comment.archived ? "bg-gray-50 border border-gray-200" : "bg-gray-50"
              )}
            >
              <div className="flex items-start gap-2">
                <Avatar className="w-6 h-6">
                  <AvatarImage src={comment.author.avatar_url || undefined} />
                  <AvatarFallback>{comment.author.student_name[0]}</AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <div className="flex justify-between items-start">
                    <span className="font-medium text-xs">{comment.author.student_name}</span>
                    <span className="text-xs text-gray-500">
                      {format(new Date(comment.created_at), "d MMM yyyy", { locale: es })}
                    </span>
                  </div>
                  <p className="mt-1 text-gray-700">{comment.content}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};
